// Поля «как гасить долг»: способ и его значение. Одна пара на весь продукт.
//
// ## Почему в общем слое, а не внутри формы
//
// Спрашивают это ДВА раза: при заведении долга в карточке человека и при смене графика в
// карточке долга. Разделы друг к другу за кусками форм не ходят, а вторая такая пара рядом
// означала бы второй разбор значения — и вот там ошибиться уже дорого.
//
// Дорого вот почему: значение графика меняет ЕДИНИЦУ вместе со способом. У «фиксированной
// суммы» это рубли, у «равных долей» — месяцы, у процента — проценты. Разъехавшись, две формы
// отправили бы одно и то же число в разных единицах: человек вписал бы 3 в поле «месяцев», а
// вторая форма отправила бы 3 копейки за расчёт, и долг в 30 000 ₽ гасился бы вечно.
//
// Поэтому единицы не решаются здесь вовсе. Подпись поля, пояснение и вид значения приходят с
// сервера (`GET /api/v1/debt-schedule-types`) — оттуда же, откуда сам список способов.
//
// ## Чего здесь нет
//
// Нет основания: у заведения долга и у смены графика оно спрашивается по-разному (там про сам
// долг, здесь про изменение условий), и общее поле пришлось бы подписывать двумя способами.
// Нет кнопок — их ставит форма, которая знает, чем кончается дело.

import { RiErrorWarningLine } from '@remixicon/react';

import type { DebtScheduleTypeOut } from '@/shared/api/endpoints/losses';
import { formatRubles } from '@/shared/money/format';
import { parseRublesToKopecks } from '@/shared/money/parse';
import type { ListResponse } from '@/shared/api/types';
import type { ApiError } from '@/shared/errors/api-error';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';
import * as Hint from '@/shared/ui/hint';

/** Что человек набрал в двух полях. Строками — ровно так, как он это видит. */
export interface СостояниеГрафика {
  тип: string;
  значение: string;
}

export const ПУСТОЙ_ГРАФИК: СостояниеГрафика = { тип: NO_VALUE, значение: '' };

/** Что уедет на сервер, либо жалоба по-русски. */
export type РазборГрафика =
  | { ok: true; schedule_type: string; schedule_value: number | null }
  | { ok: false; поле: 'тип' | 'значение'; ошибка: string };

/**
 * Разобрать набранное по правилам выбранного способа.
 *
 * Отдельной функцией, а не внутри компонента: её гоняют проверки без отрисовки, и она же
 * нужна форме до отправки. Правила единиц берутся из справочника — здесь их нет ни одного.
 */
export function разобратьГрафик(
  состояние: СостояниеГрафика,
  способы: DebtScheduleTypeOut[],
): РазборГрафика {
  if (состояние.тип === NO_VALUE || состояние.тип === '') {
    return { ok: false, поле: 'тип', ошибка: 'Выберите, как гасить долг.' };
  }
  const способ = способы.find((строка) => строка.code === состояние.тип);
  if (способ === undefined) {
    // Справочник ещё едет либо способ пропал из контракта. Отправлять вслепую нельзя:
    // сервер отвергнет, а человек прочитает это как поломку формы.
    return { ok: false, поле: 'тип', ошибка: 'Способ ещё не загрузился, повторите через миг.' };
  }

  if (способ.value_kind === 'none') {
    return { ok: true, schedule_type: способ.code, schedule_value: null };
  }

  const набрано = состояние.значение.trim();

  if (способ.value_kind === 'kopecks') {
    // Пусто — законный ответ: сервер возьмёт умолчание для типа долга, а если умолчания нет,
    // скажет об этом сам. Запрещать пустое здесь значило бы мешать завести долг, по которому
    // ещё не договорились, — а такой долг лучше видеть в панели проверки, чем не завести.
    if (набрано === '') {
      return { ok: true, schedule_type: способ.code, schedule_value: null };
    }
    const разбор = parseRublesToKopecks(набрано);
    if (!разбор.ok) {
      return { ok: false, поле: 'значение', ошибка: 'Сумма пишется числом: 1 000 или 1 000,50.' };
    }
    if (разбор.value <= 0) {
      return {
        ok: false,
        поле: 'значение',
        ошибка: 'Ноль означает «не удерживать»: оставьте поле пустым либо укажите сумму.',
      };
    }
    return { ok: true, schedule_type: способ.code, schedule_value: разбор.value };
  }

  if (набрано === '') {
    return {
      ok: false,
      поле: 'значение',
      ошибка:
        способ.value_kind === 'months'
          ? 'Укажите, за сколько месяцев гасить долг.'
          : 'Укажите, какой процент от выплаты удерживать.',
    };
  }
  if (!/^\d{1,4}$/.test(набрано)) {
    return {
      ok: false,
      поле: 'значение',
      ошибка:
        способ.value_kind === 'months'
          ? 'Месяцы пишутся целым числом: 3, 6, 12.'
          : 'Процент пишется целым числом: 10, 25, 50.',
    };
  }
  const число = Number(набрано);
  if (способ.value_kind === 'months' && (число < 1 || число > 120)) {
    return { ok: false, поле: 'значение', ошибка: 'Месяцев бывает от 1 до 120.' };
  }
  if (способ.value_kind === 'percent' && (число < 1 || число > 100)) {
    return { ok: false, поле: 'значение', ошибка: 'Процент бывает от 1 до 100.' };
  }
  return { ok: true, schedule_type: способ.code, schedule_value: число };
}

interface Свойства {
  состояние: СостояниеГрафика;
  onChange: (следующее: СостояниеГрафика) => void;
  способы: ListResponse<DebtScheduleTypeOut> | undefined;
  загружаются: boolean;
  ошибкаСправочника: ApiError | null;
  /** Умолчание графика для выбранного типа долга, копейки. Пусто — умолчания нет. */
  умолчаниеКопеек?: number | null;
  ошибки?: { тип?: string; значение?: string };
  /** Приставка к опознавателям полей: на одном экране форма бывает не одна. */
  id: string;
}

export function ПоляГрафика({
  состояние,
  onChange,
  способы,
  загружаются,
  ошибкаСправочника,
  умолчаниеКопеек,
  ошибки,
  id,
}: Свойства) {
  const способ = (способы?.items ?? []).find((строка) => строка.code === состояние.тип);
  const нужноЗначение = способ !== undefined && способ.value_kind !== 'none';

  // Предупреждение ровно про один случай: фиксированная сумма, поле пустое и умолчания у типа
  // долга нет. Тогда каскад не удержит ни рубля — не «в следующий раз», а никогда, пока
  // график не задан. Молчать об этом нельзя: долг выглядит заведённым и висит месяцами.
  const безУдержаний =
    способ?.value_kind === 'kopecks' &&
    состояние.значение.trim() === '' &&
    (умолчаниеКопеек === null || умолчаниеКопеек === undefined);

  return (
    <>
      <SelectField
        id={`${id}-schedule-type`}
        label='Как гасить'
        required
        value={состояние.тип}
        onChange={(тип) => onChange({ тип, значение: '' })}
        options={(способы?.items ?? []).map((строка) => ({
          value: строка.code,
          label: строка.title,
        }))}
        placeholder='выберите способ'
        pending={загружаются}
        failed={ошибкаСправочника !== null}
        failedError={ошибкаСправочника}
        emptyNote='Способов гашения нет. Так не бывает: они приходят из контракта, значит система развёрнута не до конца.'
        hint={способ?.hint}
        error={ошибки?.тип}
      />
      {нужноЗначение && (
        <TextField
          id={`${id}-schedule-value`}
          label={способ.value_label}
          required={способ.value_kind !== 'kopecks'}
          value={состояние.значение}
          onChange={(значение) => onChange({ ...состояние, значение })}
          placeholder={
            способ.value_kind === 'kopecks' ? '1 000' : способ.value_kind === 'months' ? '6' : '25'
          }
          inputMode={способ.value_kind === 'kopecks' ? 'decimal' : 'numeric'}
          hint={
            способ.value_kind === 'kopecks'
              ? умолчаниеКопеек
                ? `Оставьте пусто — будем удерживать ${formatRubles(умолчаниеКопеек)} в месяц, как принято для этого типа долга. Если у человека в месяце несколько ведомостей, сумма удержится один раз.`
                : 'Без суммы по этому долгу не удержится ничего: он будет висеть, пока сумму не укажут.'
              : undefined
          }
          error={ошибки?.значение}
        />
      )}
      {безУдержаний && (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          Пока сумма не указана, с человека не удержат ни рубля — долг будет висеть и попадёт в
          панель проверки при каждом расчёте.
        </Hint.Root>
      )}
    </>
  );
}
